import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  HubSection,
  RecencyBadge,
  EmptyState,
  ErrorState,
  LoadingState,
  humanise,
} from "@/components/research-hub";

// Phase E — "What the hub remembers about you".
// A calm, read-and-delete-only panel over the per-user memory the assistant keeps to
// personalise answers. No editing, no ids, no embeddings, no raw JSON on screen.

type Row = Record<string, any>;

// Loosely typed — the table fields are accessed defensively below.
type MemoryRow = {
  id: string;
  memory_text?: string | null;
  memory_kind?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  last_used_at?: string | null;
};

const MEMORY_KEY = ["research_hub_user_memory"];

export default function UserMemory() {
  const qc = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<MemoryRow | null>(null);

  // Who is signed in — RLS scopes the rows to this user; we still guard the query on it.
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ["auth_user"],
    queryFn: async () => (await supabase.auth.getUser()).data.user,
  });

  const memories = useQuery({
    queryKey: [...MEMORY_KEY, me?.id],
    enabled: !!me?.id,
    queryFn: async () => {
      // Filter to active rows SERVER-side, before the 200-row limit — otherwise inactive rows could
      // consume the limit and silently push active notes out of the page.
      const { data, error } = await (supabase as any)
        .from("research_user_memory")
        .select("id,memory_text,memory_kind,last_used_at,created_at,updated_at,is_active,user_id")
        .eq("user_id", me!.id)
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return ((data ?? []) as Row[]).filter((m) => m.is_active !== false) as MemoryRow[];
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      // .select() returns the deleted rows. RLS denies delete silently (0 rows, no error), so treat an
      // empty result as a failure rather than telling the user it worked.
      const { data, error } = await (supabase as any)
        .from("research_user_memory")
        .delete()
        .eq("id", id)
        .select("id");
      if (error) throw error;
      if (!Array.isArray(data) || data.length === 0) throw new Error("not_deleted");
    },
    onSuccess: () => {
      toast.success("Forgotten. The hub will no longer use that note.");
      qc.invalidateQueries({ queryKey: MEMORY_KEY });
    },
    onError: () => toast.error("We couldn't remove that just now. Please try again."),
    onSettled: () => setPendingDelete(null),
  });

  const items = memories.data ?? [];

  return (
    <div className="space-y-6 max-w-2xl">
      <HubSection
        title="What the hub remembers about you"
        description="These are the things the hub has noted to personalise your answers."
      >
        {meLoading || memories.isLoading || (!!me?.id && !memories.isFetched) ? (
          // Wait for the auth query AND the first memories fetch — a disabled query reports
          // isLoading=false in react-query v5, which would otherwise flash the empty state.
          <LoadingState rows={3} />
        ) : memories.error ? (
          <ErrorState onRetry={() => memories.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState title="The hub hasn't noted anything about you yet." />
        ) : (
          <div className="space-y-2">
            {items.map((m, i) => (
              <MemoryItem key={m.id ?? i} item={m} onDelete={() => setPendingDelete(m)} />
            ))}
          </div>
        )}
      </HubSection>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => {
          if (!o && !remove.isPending) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Forget this note?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              The hub will stop using this to personalise your answers. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingDelete?.memory_text && (
            <p className="font-body text-sm text-foreground border-l-2 border-border pl-3">
              {pendingDelete.memory_text}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending} className="font-body">
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              className="font-body bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                if (pendingDelete?.id) remove.mutate(pendingDelete.id);
              }}
            >
              {remove.isPending && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" aria-hidden />}
              Forget it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MemoryItem({ item, onDelete }: { item: MemoryRow; onDelete: () => void }) {
  const text = (item.memory_text ?? "").toString().trim();
  const kind = item.memory_kind ? humanise(item.memory_kind) : "";
  const noted = item.updated_at ?? item.created_at ?? null;

  return (
    <Card className="p-4 flex items-start justify-between gap-3">
      <div className="min-w-0 space-y-1.5">
        <p className="font-body text-sm text-foreground">{text || "A note the hub kept."}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {kind && <span className="font-body text-[11px] text-muted-foreground">{kind}</span>}
          {kind && noted && <span className="text-muted-foreground/40">·</span>}
          {noted && <RecencyBadge date={noted} />}
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={onDelete}
        aria-label="Forget this note"
      >
        <X className="h-4 w-4" aria-hidden />
      </Button>
    </Card>
  );
}
